import { Dialog as ChakraDialog, CloseButton, Portal } from "@chakra-ui/react";
import { forwardRef } from "react";

interface DialogContentProps extends ChakraDialog.ContentProps {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
  backdrop?: boolean;
}

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  function DialogContent(props, ref) {
    const {
      children,
      portalled = true,
      portalRef,
      backdrop = true,
      ...rest
    } = props;

    return (
      <Portal disabled={!portalled} container={portalRef}>
        {backdrop && <ChakraDialog.Backdrop />}
        <ChakraDialog.Positioner>
          <ChakraDialog.Content
            ref={ref}
            mx={{ base: 3, md: "auto" }}
            css={{
              "& > form": {
                display: "flex",
                flexDirection: "column",
                flex: "1 1 auto",
                minHeight: 0,
              },
            }}
            {...rest}
            asChild={false}
          >
            {children}
          </ChakraDialog.Content>
        </ChakraDialog.Positioner>
      </Portal>
    );
  },
);

export const DialogCloseTrigger = forwardRef<
  HTMLButtonElement,
  ChakraDialog.CloseTriggerProps
>(function DialogCloseTrigger(props, ref) {
  return (
    <ChakraDialog.CloseTrigger
      position="absolute"
      top="3"
      insetEnd="3"
      {...props}
      asChild
    >
      <CloseButton size="sm" aria-label="Cerrar" ref={ref}>
        {props.children}
      </CloseButton>
    </ChakraDialog.CloseTrigger>
  );
});

export interface DialogRootProps
  extends Omit<ChakraDialog.RootProps, "open" | "onOpenChange"> {
  isOpen: boolean;
  onClose: () => void;
  isCentered?: boolean;
}

export const DialogRoot = ({
  isOpen,
  onClose,
  isCentered,
  children,
  ...rest
}: DialogRootProps) => (
  <ChakraDialog.Root
    open={isOpen}
    onOpenChange={(e) => {
      if (!e.open) onClose();
    }}
    placement={isCentered ? "center" : "top"}
    scrollBehavior="inside"
    lazyMount
    unmountOnExit
    {...rest}
  >
    {children}
  </ChakraDialog.Root>
);

export const DialogFooter = ChakraDialog.Footer;
export const DialogHeader = ChakraDialog.Header;
export const DialogBody = ChakraDialog.Body;
export const DialogBackdrop = ChakraDialog.Backdrop;
export const DialogTitle = ChakraDialog.Title;
export const DialogDescription = ChakraDialog.Description;
export const DialogTrigger = ChakraDialog.Trigger;
export const DialogActionTrigger = ChakraDialog.ActionTrigger;
